import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import AuthContext from '../../context/authContext';
import AiProposalDecisionDialog from './AiProposalDecisionDialog';
import { AI_PROPOSAL_STATUS_CHIP_COLORS, getAiProposalStatusLabel } from './aiProposalStatus';

function formatDateTime(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

// Small label/value pair used in the metadata section
function MetaField({ label, children }) {
  return (
    <Box>
      <Typography variant='caption' color='text.secondary' component='div'>
        {label}
      </Typography>
      <Typography variant='body2' component='div' sx={{ wordBreak: 'break-word' }}>
        {children ?? '—'}
      </Typography>
    </Box>
  );
}

// Shared detail dialog for an AI change proposal. Receives the FULL proposal
// document (including action.body and currentStateSnapshot); the parent is
// responsible for fetching it. When the proposal is pending, the parent passes
// onApprove/onReject callbacks and the user holds the matching rights, decision
// buttons are shown that open a small note/confirm step.
function AiProposalDetailDialog({
  open,
  proposal,
  loading = false,
  onClose,
  onApprove = null,
  onReject = null,
}) {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const [decisionMode, setDecisionMode] = useState(null);

  // Reset any pending decision step whenever the dialog opens for another proposal
  useEffect(() => {
    setDecisionMode(null);
  }, [open, proposal?._id]);

  const canApprove = authCtx.checkRight('aiProposal:approve');
  const canReject = authCtx.checkRight('aiProposal:reject');
  const isPending = proposal?.status === 'pending';
  const showApprove = Boolean(isPending && typeof onApprove === 'function' && canApprove);
  const showReject = Boolean(isPending && typeof onReject === 'function' && canReject);

  const handleDecisionConfirm = useCallback(
    async note => {
      if (decisionMode === 'approve' && typeof onApprove === 'function') {
        await onApprove(note);
      } else if (decisionMode === 'reject' && typeof onReject === 'function') {
        await onReject(note);
      }
      setDecisionMode(null);
    },
    [decisionMode, onApprove, onReject]
  );

  const preSx = useMemo(
    () => ({
      m: 0,
      p: { xs: 1.5, sm: 2 },
      borderRadius: 1,
      backgroundColor:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : alpha(theme.palette.common.white, 0.04),
      border: `1px solid ${theme.palette.divider}`,
      overflow: 'auto',
      fontSize: { xs: '0.7rem', sm: '0.8rem' },
      maxHeight: '50vh',
    }),
    [theme]
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
        <DialogTitle>KI-Vorschlag Details</DialogTitle>
        <DialogContent dividers>
          {loading || !proposal ? (
            <Typography variant='body2'>
              {loading ? 'Lade Details...' : 'Keine Daten verfügbar.'}
            </Typography>
          ) : (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <MetaField label='Status'>
                    <Chip
                      size='small'
                      color={AI_PROPOSAL_STATUS_CHIP_COLORS[proposal.status] || 'default'}
                      label={getAiProposalStatusLabel(proposal.status)}
                    />
                  </MetaField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <MetaField label='Service-Account'>
                    {proposal.serviceAccountName || proposal.serviceAccountId || '—'}
                  </MetaField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <MetaField label='Domain'>{proposal.targetDomain || '—'}</MetaField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <MetaField label='Erstellt'>{formatDateTime(proposal.meta?.createdAt)}</MetaField>
                </Grid>
                <Grid item xs={12}>
                  <MetaField label='Aktion'>
                    <Typography
                      component='span'
                      variant='body2'
                      sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                    >
                      {proposal.action?.method || '—'} {proposal.action?.path || ''}
                    </Typography>
                  </MetaField>
                </Grid>
                <Grid item xs={12}>
                  <MetaField label='Zusammenfassung'>{proposal.summary || '—'}</MetaField>
                </Grid>
                <Grid item xs={12}>
                  <MetaField label='Begründung'>{proposal.reason || '—'}</MetaField>
                </Grid>
                {proposal.decision ? (
                  <>
                    <Grid item xs={6} sm={3}>
                      <MetaField label='Entschieden von'>
                        {proposal.decision.decidedByName || proposal.decision.decidedBy || '—'}
                      </MetaField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <MetaField label='Entschieden am'>
                        {formatDateTime(proposal.decision.decidedAt)}
                      </MetaField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MetaField label='Notiz'>{proposal.decision.note || '—'}</MetaField>
                    </Grid>
                  </>
                ) : null}
                {proposal.applyResult ? (
                  <>
                    <Grid item xs={6} sm={3}>
                      <MetaField label='Statuscode'>
                        {proposal.applyResult.statusCode ?? '—'}
                      </MetaField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <MetaField label='Angewendet am'>
                        {formatDateTime(proposal.applyResult.appliedAt)}
                      </MetaField>
                    </Grid>
                    {proposal.applyResult.error ? (
                      <Grid item xs={12} sm={6}>
                        <MetaField label='Fehler'>
                          <Typography component='span' variant='body2' color='error'>
                            {proposal.applyResult.error}
                          </Typography>
                        </MetaField>
                      </Grid>
                    ) : null}
                  </>
                ) : null}
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Aktueller Zustand
                  </Typography>
                  <Box component='pre' sx={preSx}>
                    {proposal.currentStateSnapshot
                      ? JSON.stringify(proposal.currentStateSnapshot, null, 2)
                      : '–'}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Vorgeschlagene Änderung
                  </Typography>
                  <Box component='pre' sx={preSx}>
                    {proposal.action?.body ? JSON.stringify(proposal.action.body, null, 2) : '–'}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {showReject && (
            <Button color='error' startIcon={<CloseIcon />} onClick={() => setDecisionMode('reject')}>
              Ablehnen
            </Button>
          )}
          {showApprove && (
            <Button
              color='success'
              startIcon={<CheckIcon />}
              onClick={() => setDecisionMode('approve')}
            >
              Annehmen
            </Button>
          )}
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <AiProposalDecisionDialog
        open={Boolean(decisionMode)}
        mode={decisionMode || 'approve'}
        summary={proposal?.summary || ''}
        onCancel={() => setDecisionMode(null)}
        onConfirm={handleDecisionConfirm}
      />
    </>
  );
}

export default AiProposalDetailDialog;
